import React, { useState, useEffect } from "react";
import {
  Button,
  Spinner,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import DataTable from "react-data-table-component";
import {
  Add,
  EditExpenseTitle,
  ExpenseTitle,
  Submit,
} from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, mediaUrl, uploadFile } from "../../../Utils/FileUpload";
import { Expense } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useCustomToasts from "../../../hooks/useCustomToast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
interface FormValues {
  title: string;
  description?: string;
  amount: number | null;
  date: Date | string;
}
interface ErrorResponse {
  message: string;
}
export default function EditExpense() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: "",
      description: "",
      amount: null,
      date: "",
    },
  });
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Expense[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}expense/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data: Expense[] = response.data;
        setFilteredItems(data);

        if (location.state?.id) {
          // make sure id exists
          const result = await axios.post(
            `${baseUrl}expense/viewbyid`,
            { id: location.state.id },
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setValue("title", result.data.title);
          setValue("date", result.data.date);
          setValue("description", result.data.description);
          const formatAmount = (value: number | string): number => {
            const num = parseFloat(value.toString());
            return num % 1 === 0 ? Math.floor(num) : parseFloat(num.toFixed(2));
          };
          setValue("amount", formatAmount(result.data.amount));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.state?.id, setValue, refresh]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const editdata = {
        title: data.title,
        description: data.description,
        date: new Date(data.date).toISOString().split("T")[0], // Formats to 'YYYY-MM-DD'
        amount: data.amount,
        id: location.state.id,
      };
      // console.log(editdata, "editdata");
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseUrl}expense/update`, editdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        SuccessToast("Data updated successfully", "/view_expense");
      } else {
        ErrorToast("Failed to update");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorResponse = axiosError.response?.data as ErrorResponse;
      const errorMessage =
        errorResponse?.message || "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const filteredData = filteredItems.filter((item) => {
    const filter = filterText.toLowerCase();
    return (
      (item.title && item.title.toLowerCase().includes(filter)) ||
      (item.date && item.date.toString().toLowerCase().includes(filter)) ||
      (item.amount && item.amount.toString().toLowerCase().includes(filter))
    );
  });

  const handleEdit = (id: string) => {
    // console.log(id,"id")
    navigate("/edit_expense", { state: { id: id } });
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this expense?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}expense/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Expense Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setFilteredItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
      } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error("An error occurred while deleting the expense", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const formatAmount = (value: number | string): string => {
    const num = parseFloat(value.toString());
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  };
  // Define columns with actions
  const expenseTableColumns = [
    {
      name: "Title",
      selector: (row: Expense) => row.title,
      sortable: true,
      width: "25%",
    },
    {
      name: "Date",
      selector: (row: Expense) => row.date.split(" ")[0], // only date part
      sortable: true,
      width: "25%",
    },
    {
      name: "Amount",
      selector: (row: Expense) => formatAmount(row.amount),
      sortable: true,
      width: "25%",
    },
    {
      name: "Actions",
      cell: (row: Expense) => (
        <div className="d-flex justify-content-between">
          <Button
            color="primary"
            size="sm"
            onClick={() => handleEdit(row.id)}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit"
          >
            <i className="icon-pencil-alt" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
            disabled={disabledDelete === row.id || disableAllDelete}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Delete"
          >
            <i className="icon-trash" />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      // button: true,
      width: "25%",
    },
  ];

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };
  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={EditExpenseTitle}
        parent={ExpenseTitle}
        title={EditExpenseTitle}
      />
      <Row>
        <Col xl={4} className="mb-lg-3">
          <Card>
            <CardBody className="custom-input form-validation">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xs={12}>
                    <Label htmlFor="expense-title">
                      Title <small className="text-danger">*</small>
                    </Label>
                    <Controller
                      name="title"
                      control={control}
                      rules={{ required: "Title is required" }}
                      render={({ field }) => (
                        <>
                          <Input
                            id="expense-title"
                            type="text"
                            placeholder="Expense Title"
                            {...field}
                            invalid={!!errors.title}
                            onKeyDown={handleKeyDown}
                          />
                          {errors.title && (
                            <FormFeedback>{errors.title.message}</FormFeedback>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <Label htmlFor="expense-description">Description</Label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="expense-description"
                          type="textarea"
                          placeholder="Description"
                          {...field}
                          onKeyDown={handleKeyDown}
                        />
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <Label htmlFor="expense-amount">
                      Amount <small className="text-danger">*</small>
                    </Label>
                    <Controller
                      name="amount"
                      control={control}
                      rules={{
                        required: "Amount is required",
                        validate: (value: any) =>
                          value > 0 || "Amount must be greater than 0",
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            id="expense-amount"
                            type="number"
                            placeholder="Amount"
                            {...field}
                            value={field.value ?? ""}
                            invalid={!!errors.amount}
                            onKeyDown={(e) => {
                              handleKeyDown(e);
                              handleAmountKeyDown(e);
                            }}
                            onWheel={(e) =>
                              (e.target as HTMLInputElement).blur()
                            }
                          />
                          {errors.amount && (
                            <FormFeedback>{errors.amount.message}</FormFeedback>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <Label htmlFor="expense-date">
                      Date <small className="text-danger">*</small>
                    </Label>
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: "Date is required" }}
                      render={({ field }) => (
                        <>
                          <DatePicker
                            selected={
                              field.value ? new Date(field.value) : null
                            }
                            onChange={(date) => field.onChange(date)}
                            placeholderText="Select date"
                            className={`form-control ${
                              errors.date ? "is-invalid" : ""
                            }`}
                            dateFormat="dd-MM-yyyy"
                            maxDate={new Date()}
                          />
                          {errors.date && (
                            <div className="invalid-feedback d-block">
                              {errors.date.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12} className="mt-2">
                    <Btn color="primary" disabled={isButtonDisabled}>
                      Submit
                    </Btn>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
        <Col xl={8} className="mb-lg-3">
          <Card>
            <CardBody>
              <div className="mb-3">
                <FilterComponent
                  onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilterText(e.target.value)
                  }
                  filterText={filterText}
                />
              </div>
              <div className="table-responsive">
                <DataTable
                  data={filteredData}
                  columns={expenseTableColumns}
                  pagination
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
